import { create } from "zustand";

export interface ScoringProgress {
  scored: number;
  total: number;
  currentBatch: number;
  totalBatches: number;
  percent: number;
  statusMessage: string;
}

export type ScoringMode = "standard" | "fulltext_rescore";

interface ScoringState {
  isScoring: boolean;
  isCompleted: boolean;
  error: string | null;
  jobId: string | null;
  mode: ScoringMode;
  progress: ScoringProgress;
  // Tier 4 hybrid batch: set when the SSE terminal event is
  // scoring_batch_submitted (head scored interactively, tail riding a
  // Message Batch). The durable scoring_batch_jobs row — polled via
  // useScoringBatchStatus, not this field — drives the OperationBar pill;
  // this just records that a background tail exists for this run.
  backgroundBatch: { tailTotal: number } | null;

  startScoring: (jobId: string, mode: ScoringMode, total: number) => void;
  updateProgress: (update: Partial<ScoringProgress>) => void;
  completeScoring: (message: string) => void;
  failScoring: (error: string) => void;
  startBackgroundBatch: (tailTotal: number) => void;
  clearBackgroundBatch: () => void;
  resetScoring: () => void;
}

const initialProgress: ScoringProgress = {
  scored: 0,
  total: 0,
  currentBatch: 0,
  totalBatches: 0,
  percent: 0,
  statusMessage: "",
};

export const useScoringStore = create<ScoringState>((set, get) => ({
  isScoring: false,
  isCompleted: false,
  error: null,
  jobId: null,
  mode: "standard",
  progress: { ...initialProgress },
  backgroundBatch: null,

  startScoring: (jobId, mode, total) =>
    set({
      isScoring: true,
      isCompleted: false,
      error: null,
      jobId,
      mode,
      progress: { ...initialProgress, total, statusMessage: "Starting scoring..." },
    }),

  updateProgress: (update) =>
    set((state) => ({
      progress: { ...state.progress, ...update },
    })),

  completeScoring: (message) =>
    set({
      isScoring: false,
      isCompleted: true,
      progress: { ...get().progress, statusMessage: message, percent: 100 },
    }),

  failScoring: (error) =>
    set({
      isScoring: false,
      error,
    }),

  startBackgroundBatch: (tailTotal) =>
    set({
      backgroundBatch: { tailTotal },
      isScoring: false,
      isCompleted: true,
    }),

  clearBackgroundBatch: () => set({ backgroundBatch: null }),

  resetScoring: () =>
    set({
      isScoring: false,
      isCompleted: false,
      error: null,
      jobId: null,
      mode: "standard",
      progress: { ...initialProgress },
      backgroundBatch: null,
    }),
}));
